import React, { useState, useEffect, useRef } from 'react';
import { MapPin, Loader2, X, Navigation } from 'lucide-react';

const MAPBOX_TOKEN = process.env.REACT_APP_MAPBOX_TOKEN;

// Debounce helper
function useDebounce(value, delay) {
  const [debouncedValue, setDebouncedValue] = useState(value);
  
  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);
    
    return () => clearTimeout(handler);
  }, [value, delay]);
  
  return debouncedValue;
}

export function AddressAutocomplete({ onSelect, placeholder, className }) {
  const [query, setQuery] = useState('');
  const [suggestions, setSuggestions] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [selectedPlace, setSelectedPlace] = useState(null);
  const inputRef = useRef(null);
  const containerRef = useRef(null);
  
  const debouncedQuery = useDebounce(query, 300);
  
  // Fetch suggestions from Mapbox
  useEffect(() => {
    if (!debouncedQuery || debouncedQuery.length < 2) {
      setSuggestions([]);
      return;
    }
    
    async function fetchSuggestions() {
      setIsLoading(true);
      try {
        // Mapbox Geocoding API - search for addresses, places, postcodes, regions
        const types = 'address,place,postcode,region,district';
        const url = `https://api.mapbox.com/geocoding/v5/mapbox.places/${encodeURIComponent(debouncedQuery)}.json?access_token=${MAPBOX_TOKEN}&country=us&types=${types}&limit=7&autocomplete=true`;
        
        const response = await fetch(url);
        const data = await response.json();
        
        if (data.features) {
          const formattedSuggestions = data.features.map(feature => {
            // Determine the type of place
            let placeType = 'address';
            if (feature.place_type.includes('postcode')) {
              placeType = 'zip';
            } else if (feature.place_type.includes('region')) {
              placeType = 'state';
            } else if (feature.place_type.includes('district')) {
              placeType = 'county';
            } else if (feature.place_type.includes('place')) {
              placeType = 'city';
            }
            
            // Extract state code from context
            let stateCode = null;
            let stateName = null;
            let county = null;
            let city = null;
            
            if (feature.context) {
              for (const ctx of feature.context) {
                if (ctx.id.startsWith('region')) {
                  stateName = ctx.text;
                  stateCode = ctx.short_code?.replace('US-', '');
                }
                if (ctx.id.startsWith('district')) {
                  county = ctx.text?.replace(' County', '');
                }
                if (ctx.id.startsWith('place')) {
                  city = ctx.text;
                }
              }
            }
            
            // For state-level searches, extract from the feature itself
            if (placeType === 'state') {
              stateName = feature.text;
              stateCode = feature.properties?.short_code?.replace('US-', '');
            }
            
            // For county-level searches, extract county from the feature itself
            if (placeType === 'county') {
              // feature.text is like "Collin County" - remove " County" suffix for matching
              county = feature.text?.replace(' County', '');
            }
            
            return {
              id: feature.id,
              name: feature.place_name,
              shortName: feature.text,
              placeType,
              coordinates: feature.center, // [lng, lat]
              bbox: feature.bbox,
              stateCode,
              stateName,
              county,
              city,
              raw: feature
            };
          });
          
          setSuggestions(formattedSuggestions);
        }
      } catch (error) {
        console.error('Error fetching suggestions:', error);
        setSuggestions([]);
      } finally {
        setIsLoading(false);
      }
    }
    
    fetchSuggestions();
  }, [debouncedQuery]);
  
  // Handle click outside to close suggestions
  useEffect(() => {
    function handleClickOutside(event) {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setShowSuggestions(false);
      }
    }
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);
  
  function handleSelect(suggestion) {
    setQuery(suggestion.name);
    setSelectedPlace(suggestion);
    setShowSuggestions(false);
    
    if (onSelect) {
      onSelect(suggestion);
    }
  }
  
  function handleClear() {
    setQuery('');
    setSelectedPlace(null);
    setSuggestions([]);
    if (onSelect) {
      onSelect(null);
    }
    inputRef.current?.focus();
  }
  
  function getPlaceTypeIcon(placeType) {
    switch (placeType) {
      case 'state':
        return <span className="text-[10px] font-bold text-purple-400 bg-purple-500/20 px-1.5 py-0.5 rounded">STATE</span>;
      case 'county':
        return <span className="text-[10px] font-bold text-blue-400 bg-blue-500/20 px-1.5 py-0.5 rounded">COUNTY</span>;
      case 'city':
        return <span className="text-[10px] font-bold text-teal-400 bg-teal-500/20 px-1.5 py-0.5 rounded">CITY</span>;
      case 'zip':
        return <span className="text-[10px] font-bold text-amber-400 bg-amber-500/20 px-1.5 py-0.5 rounded">ZIP</span>;
      default:
        return <span className="text-[10px] font-bold text-emerald-400 bg-emerald-500/20 px-1.5 py-0.5 rounded">ADDRESS</span>;
    }
  }
  
  return (
    <div ref={containerRef} className="relative w-full">
      <div className="relative">
        <label htmlFor="address-search" className="sr-only">Search location</label>
        <MapPin className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-slate-400 dark:text-slate-500" />
        <input
          ref={inputRef}
          id="address-search"
          name="address-search"
          type="text"
          value={query}
          onChange={(e) => {
            setQuery(e.target.value);
            setShowSuggestions(true);
            setSelectedPlace(null);
          }}
          onFocus={() => setShowSuggestions(true)}
          placeholder={placeholder || "Search address, city, county, ZIP, or state..."}
          className={`w-full h-14 pl-12 pr-12 rounded-xl bg-white dark:bg-slate-900/80 border border-slate-300 dark:border-slate-700 text-slate-900 dark:text-white text-lg placeholder:text-slate-400 dark:placeholder:text-slate-500 focus:border-teal-500 focus:outline-none focus:ring-2 focus:ring-teal-500/20 transition-all ${className || ''}`}
          data-testid="address-autocomplete-input"
          autoComplete="off"
          aria-label="Search location"
          aria-autocomplete="list"
          aria-controls="address-suggestions"
        />
        {isLoading && (
          <Loader2 className="absolute right-4 top-1/2 -translate-y-1/2 h-5 w-5 text-slate-400 dark:text-slate-500 animate-spin" />
        )}
        {!isLoading && query && (
          <button
            type="button"
            onClick={handleClear}
            className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 dark:text-slate-500 dark:hover:text-slate-300 transition-colors"
          >
            <X className="h-5 w-5" />
          </button>
        )}
      </div>
      
      {/* Suggestions Dropdown */}
      {showSuggestions && suggestions.length > 0 && (
        <div className="absolute z-50 w-full mt-2 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-xl shadow-xl overflow-hidden" data-testid="suggestions-dropdown">
          <div className="py-2 overflow-y-auto max-h-52">
            {suggestions.map((suggestion) => {
              // For addresses, show the full address as primary text
              const primaryText = suggestion.placeType === 'address' 
                ? suggestion.name.split(',')[0] // First part of address (street)
                : suggestion.shortName;
              const secondaryText = suggestion.placeType === 'address'
                ? suggestion.name // Full address
                : suggestion.name;
              
              return (
                <button
                  key={suggestion.id}
                  type="button"
                  onClick={() => handleSelect(suggestion)}
                  className="w-full px-4 py-3 text-left hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors flex items-start gap-3"
                  data-testid={`suggestion-${suggestion.id}`}
                >
                  <Navigation className="h-4 w-4 text-slate-400 dark:text-slate-500 mt-1 flex-shrink-0" />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-slate-900 dark:text-white font-medium">{primaryText}</span>
                      {getPlaceTypeIcon(suggestion.placeType)}
                    </div>
                    <p className="text-sm text-slate-500 dark:text-slate-400 truncate">{secondaryText}</p>
                  </div>
                </button>
              );
            })}
          </div>
        </div>
      )}
      
      {/* No results message */}
      {showSuggestions && query.length >= 2 && !isLoading && suggestions.length === 0 && (
        <div className="absolute z-50 w-full mt-2 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-xl shadow-xl p-4 text-center">
          <p className="text-slate-500 dark:text-slate-400">No locations found. Try a different search.</p>
        </div>
      )}
    </div>
  );
}

export default AddressAutocomplete;
